import React, { createContext, useContext, useMemo } from 'react';
import { FormLabel } from 'react-bootstrap';
import { ExclamationTriangleFill } from 'react-bootstrap-icons';

// Generic field highlight tones — usable for QA, validation, or any future issue type
export const FIELD_TONE_STYLES = {
  error: {
    label: { color: '#FF0000', fontWeight: 'bold' },
    input: { borderColor: '#FF0000', backgroundColor: '#fff5f5' },
    iconColor: '#FF0000'
  },
  warning: {
    label: { color: '#FF7900', fontWeight: 'bold' },
    input: { borderColor: '#FF7900', backgroundColor: '#fffaf0' },
    iconColor: '#FF7900'
  },
  default: {
    label: { color: '#495057', fontWeight: 'bold' },
    input: { borderColor: '#adb5bd', backgroundColor: '#f8f9fa' },
    iconColor: '#6c757d'
  }
};

export const getFieldStatusStyles = tone => (tone ? FIELD_TONE_STYLES[tone] : null);

const FieldStatusContext = createContext(null);

export const FieldStatusProvider = ({ statuses = {}, children }) => {
  const value = useMemo(() => statuses, [statuses]);
  return <FieldStatusContext.Provider value={value}>{children}</FieldStatusContext.Provider>;
};

export const useFieldStatus = fieldKey => {
  const statuses = useContext(FieldStatusContext);
  if (!fieldKey || !statuses) return null;
  return statuses[fieldKey] ?? null;
};

// Wrap custom fields (not using InputCell) with the same label/input highlight behavior
export const FieldStatusShell = ({
  fieldKey,
  statuses: statusesProp,
  label,
  labelClassName = 'midFont font-weight-bold mb-0',
  children
}) => {
  const contextStatuses = useContext(FieldStatusContext);
  const statuses = statusesProp ?? contextStatuses;
  const tone = fieldKey && statuses ? statuses[fieldKey] ?? null : null;
  const styles = getFieldStatusStyles(tone);

  return (
    <div data-field-key={fieldKey}>
      {label ? (
        <div className="d-flex align-items-center mt-2">
          <FormLabel className={labelClassName} style={styles?.label}>
            {label}
          </FormLabel>
          {tone ? <ExclamationTriangleFill size={14} color={styles.iconColor} className="ml-1" /> : null}
        </div>
      ) : null}
      {typeof children === 'function' ? children(styles) : children}
    </div>
  );
};
